import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import type { Components, ExtraProps } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  PaperAirplaneIcon,
  DocumentArrowDownIcon,
  SparklesIcon,
  LinkIcon,
  StopIcon,
  ArrowPathIcon,
  PrinterIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  BookmarkIcon,
  FolderOpenIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowsRightLeftIcon,
  MapPinIcon,
  LockClosedIcon,
  XMarkIcon,
  PlusIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import { useDispatch, useSelector } from '../../../Flux/index.js'
import { chatSlice, selectGlobalContext, selectPinnedItemIds } from '../../../Flux/Slice/Chat/ChatSlice.js'
import type { IChatMessage } from '../../../Flux/Slice/Chat/ChatSlice.js'
import type { ChatNode, NodeMap } from '../../../Flux/Slice/Chat/conversationTree.js'
import { childrenOf, deepestLeaf, pathToRoot } from '../../../Flux/Slice/Chat/conversationTree.js'
import { snapshotWorkspace, hydrateFromWorkspace } from '../../../Flux/Slice/Chat/Workspace.js'
import type { WorkspaceSummary } from '../../../Flux/Slice/Chat/Workspace.js'
import { defaultWorkspaceStore } from '../../../Repository/Chat/WorkspaceStore.js'
import { ServerWorkspaceStore } from '../../../Repository/Chat/ServerWorkspaceStore.js'
import type { EvidenceRef } from '../../../Generated/EvidenceRef.ts'
import type { PinRefusal } from '../../../Generated/PinRefusal.ts'
import type { RenderedImage } from '../../../Generated/RenderedImage.ts'
import type { RenderedReport } from '../../../Generated/RenderedReport.ts'
import type { ChatDigest } from '../../../Generated/ChatDigest.ts'
import { getChatHealth, getChatDigest, shareFindings } from '../../../Repository/Chat/ChatRepository.js'
import { EvidenceView, evidenceKindMeta, fieldLabel, isHiddenField } from './EvidenceView.js'
import { remarkCiteRefs } from './remarkCiteRefs.js'
import { remarkGlossary } from './remarkGlossary.js'
import { WorkspacePanel } from './WorkspacePanel.js'
import { isCubeChartKind, directiveKey, graphLabel, formatProvenance, prettyKind } from '../../../Flux/Slice/Chat/chatViz.js'
import { visualizationSlice } from '../../../Flux/Slice/Visualization/VisualizationSlice.js'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'
import { orderSlice } from '../../../Flux/Slice/Order/OrderSlice.js'
import { Environment } from '../../../environment.js'

type ChatPageProps = { type: 'ORDER' | 'PROJECT' }

const SUGGESTIONS = [
  'How are the pollinators looking in this dataset?',
  'Which samples dominate the reads?',
  'Are there any red-listed species?',
  'Give me an overview of what was found.'
]

/** How often to re-probe the LLM backend readiness canary. */
const HEALTH_POLL_MS = 30000

/**
 * Chat-with-your-data page. One component serves both order and project scope (mirrors OtuDataTableView):
 * `type` fixes the scope kind, `useParams().id` is the scope id. Two panes: the conversation, and a
 * persistent graph canvas the model opens charts onto (and points back at via focusChart). Both live in
 * the `chat` slice; each turn POSTs to the server-side agent loop via the SendMessageEpic.
 *
 * NOTE: gated behind BETA feature access + the backend `mcp` env (spike). Non-streaming for now.
 */
export function ChatPage({ type }: ChatPageProps) {
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const scopeType = type === 'ORDER' ? 'order' : 'project'
  const scopeId = id ?? ''
  const scopeKey = `${scopeType}:${scopeId}`

  const messages = useSelector((state) => state.chat.messages)
  // W-1: the conversation TREE. We render the active root→current PATH (derived here); with no branches this is
  // exactly `messages`. The nodes map + currentNodeId also drive the per-message branch controls.
  const conversationNodes = useSelector((state) => state.chat.conversationNodes)
  const currentNodeId = useSelector((state) => state.chat.currentNodeId)
  const pathNodes = useMemo(
    () => pathToRoot(conversationNodes as NodeMap, currentNodeId),
    [conversationNodes, currentNodeId]
  )
  // Branches exist off the active path when the tree holds more nodes than the path visits.
  const hasBranches = Object.keys(conversationNodes).length > pathNodes.length
  const loading = useSelector((state) => state.chat.openRequests.length > 0)
  const activeScopeKey = useSelector((state) => state.chat.scopeKey)
  const citeSources = useSelector((state) => state.chat.citeSources)
  // The "current lens" the most recent query resolved to (blast source + SPIN/group knobs) — drives the
  // always-on indicator by the composer. Absent until the first turn resolves a context; last-wins thereafter.
  const currentLens = useSelector((state) => state.chat.currentLens)
  const currentLensText = formatProvenance(currentLens)
  // A cheap changes-as-the-answer-grows signal for auto-scroll: last message's content + step counts. Unlike
  // `messages.length` this ticks on every `appendAnswerDelta` / `addStreamStep` during a streaming turn.
  const streamSignal = useSelector((state) => {
    const msgs = state.chat.messages
    const last = msgs[msgs.length - 1]
    return last ? `${msgs.length}:${last.content.length}:${last.steps?.length ?? 0}` : ''
  })
  const lastFailedQuestion = useSelector((state) => state.chat.lastFailedQuestion)
  // "Ask about this" seed: a starter question a chart's affordance pushed into the slice. Read as (text, nonce)
  // so clicking the same chart twice re-seeds even when the text is identical (see the effect below).
  const composerSeed = useSelector((state) => state.chat.composerSeed)
  const composerSeedNonce = useSelector((state) => state.chat.composerSeedNonce)
  const [draft, setDraft] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  // Whether the message list is scrolled (near) the bottom. Drives "polite" auto-scroll: we only follow the
  // streaming answer while the user is pinned to the bottom; if they scroll up to read, we stop yanking them.
  const atBottomRef = useRef(true)

  // Reset the conversation whenever the user lands on a different order/project.
  useEffect(() => {
    if (activeScopeKey !== scopeKey) {
      dispatch(chatSlice.actions.resetConversation({ scopeKey }))
    }
  }, [scopeKey, activeScopeKey, dispatch])

  // A new message (a send, a reply landing) always jumps to the bottom and re-pins auto-follow.
  useEffect(() => {
    atBottomRef.current = true
    const el = scrollRef.current
    el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [messages.length])

  // Streaming growth (tool steps + answer deltas): follow the bottom ONLY while the user is pinned there, so a
  // 30–60s turn stays in view — but never yank a user who has scrolled up to read. Auto-follow resumes on its
  // own once they scroll back down (see `onScroll`). Instant (no smooth) so it can't fight rapid deltas.
  useEffect(() => {
    if (!atBottomRef.current) return
    const el = scrollRef.current
    el?.scrollTo({ top: el.scrollHeight })
  }, [streamSignal])

  // Receive Krona zoom updates posted from the embed iframe(s) and record them for the model's viz context.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      const data = event.data
      if (data?.type === 'krona-node' && typeof data.contextId === 'string' && Array.isArray(data.path)) {
        dispatch(chatSlice.actions.kronaNodeSelected({ contextId: data.contextId, path: data.path as string[] }))
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [dispatch])

  // Cube charts render by driving the workspace slice, which needs the scope's samples loaded first. Do
  // that bootstrap ONCE per scope, and only if a cube chart has actually been requested (avoids loading
  // samples for text-only conversations). reset() runs only here — per-chart code is purely additive.
  const openGraphs = useSelector((state) => state.chat.openGraphs)
  const needsViz = useMemo(() => openGraphs.some((d) => isCubeChartKind(d.chartKind)), [openGraphs])
  const currentVizScopeId = useSelector((state) => state.visualization.scope?.id)
  const vizBootstrappedFor = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!needsViz || !scopeId || vizBootstrappedFor.current === scopeKey) return
    vizBootstrappedFor.current = scopeKey
    // Preserve already-loaded cubes: reset() wipes the cube-tracking slice, so only reset + re-scope when a
    // DIFFERENT scope was loaded. Arriving from the workspace on THIS scope → reuse its warm cubes/sources.
    if (currentVizScopeId !== scopeId) {
      if (currentVizScopeId) dispatch(visualizationSlice.actions.reset())
      dispatch(visualizationSlice.actions.setScope({ kind: scopeType, id: scopeId }))
    }
    // Load the scope's trait vocabulary so trait-mode charts (STACKED_BAR / pie / treemap on a traitGroup)
    // can join. This dispatch otherwise lives ONLY in the full VizWorkspace, so without it every chat trait
    // chart rendered as all-"no trait data" (allTraits stayed [] → buildTraitsByTaxonMap empty → NO_TRAIT for
    // every row). The /traits fetch is scope-keyed and idempotent, so firing it on each bootstrap is safe.
    dispatch(visualizationSlice.actions.loadTraits({ scopeId }))
    // Ensure the scope's samples are in the store (BlockingSourceEpic buckets from them). Cheap vs cubes;
    // fires once per chat-scope bootstrap. Cubes themselves are keyed by (scope, blastType) and reused.
    if (scopeType === 'project') {
      dispatch(projectSlice.actions.getProjectRequest({ projectId: scopeId }))
      dispatch(projectSlice.actions.getProjectSamplesRequest({ projectId: scopeId }))
      dispatch(projectSlice.actions.loadProjectOrdersRequest({ projectId: scopeId }))
    } else {
      dispatch(orderSlice.actions.getOrderRequest({ orderId: scopeId }))
      dispatch(orderSlice.actions.getAllDatasetRequest({ orderId: scopeId }))
    }
  }, [needsViz, scopeKey, scopeType, scopeId, currentVizScopeId, dispatch])

  // Fast readiness canary for the LLM backend (server-side llama.cpp /health). Poll on mount + periodically
  // so we can show an "offline" banner and disable the composer instead of letting a turn hang/fail.
  const [llmAvailable, setLlmAvailable] = useState(true)
  useEffect(() => {
    let cancelled = false
    const check = () => void getChatHealth().then((ok) => !cancelled && setLlmAvailable(ok))
    check()
    const id = setInterval(check, HEALTH_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  // Composer fallback for a failed turn (item #9): restore the failed question into an EMPTY composer, once
  // per failure, so the typed text is never lost even if the user ignores the per-bubble Retry button. Never
  // clobbers text the user is already typing.
  const restoredRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (lastFailedQuestion && lastFailedQuestion !== restoredRef.current) {
      restoredRef.current = lastFailedQuestion
      setDraft((d) => (d.trim() ? d : lastFailedQuestion))
    }
  }, [lastFailedQuestion])

  // "Ask about this" (fast-follow): when a chart's affordance seeds a starter question, drop it into the
  // composer and focus it so the user can review/edit and hit Send — it does NOT auto-send. Guarded on the
  // nonce (not the text) so re-clicking the same chart re-seeds; this is an explicit user action, so it
  // deliberately REPLACES whatever was in the composer (unlike the gentle failed-question restore above).
  const seededNonceRef = useRef(0)
  useEffect(() => {
    if (composerSeed && composerSeedNonce !== seededNonceRef.current) {
      seededNonceRef.current = composerSeedNonce
      setDraft(composerSeed)
      composerRef.current?.focus()
    }
  }, [composerSeed, composerSeedNonce])

  // Track pinned-to-bottom so streaming auto-scroll stays polite (resumes when the user returns to the bottom).
  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  const send = (text: string) => {
    const message = text.trim()
    if (!message || loading || !scopeId || !llmAvailable) return
    dispatch(chatSlice.actions.sendMessageRequest({ scopeType, scopeId, message }))
    setDraft('')
  }

  // Stop (item #8): abort the in-flight turn. The epic's `takeUntil(cancelStream)` aborts the SSE fetch; the
  // slice closes the streaming placeholder as `stopped` (keeps the partial answer, not an error).
  const stop = () => dispatch(chatSlice.actions.cancelStream())

  // Retry (item #9): drop the failed (question + error) turn and re-send the same question as a fresh turn.
  const retry = (index: number, question: string) => {
    if (loading || !scopeId || !llmAvailable) return
    dispatch(chatSlice.actions.dropTurn({ index }))
    dispatch(chatSlice.actions.sendMessageRequest({ scopeType, scopeId, message: question }))
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      send(draft)
    }
  }

  const isEmpty = messages.length === 0

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-4 lg:flex-row">
      {/* Chat column */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-[2]">
        <div className="mb-2">
          <div className="flex items-start justify-between gap-2">
            <h1 className="text-2xl font-bold">Ask about your data</h1>
            {/* W-0: save the current chat (incl. rendered graphs + report) and resume it later, browser-only. */}
            <WorkspaceControls scopeKey={scopeKey} />
          </div>
          <p className="text-sm text-gray-500">
            Ask questions in plain language about this {scopeType}. Charts you ask about open in the panel
            beside the chat.
          </p>
          {/* W-1: subtle "you're in a branched conversation" hint — only when branches exist off the active path. */}
          {hasBranches && (
            <span
              className="badge badge-ghost badge-sm mt-1 gap-1 font-normal"
              title="This conversation has branches. Use ‹ › on a message to switch between them."
            >
              <ArrowsRightLeftIcon className="w-3.5" /> Branched conversation
            </span>
          )}
          {/* Reuses the platform's existing order report-download (same trigger as the order page) instead
              of a bespoke report — order scope only, since a project has no single report artifact. */}
          {scopeType === 'order' && scopeId && Environment.CHAT_REPORT_ENABLED && (
            <button
              type="button"
              className="btn btn-outline btn-sm mt-2"
              onClick={() => dispatch(orderSlice.actions.getReportRequest({ orderName: '', orderId: scopeId }))}
              title="Download the full lab report spreadsheet (xlsx) for this order — the same report as on the order page"
            >
              <DocumentArrowDownIcon className="w-4" /> Download full report (xlsx)
            </button>
          )}
          {/* No-login "Share findings" snapshot (D-share, roadmap #6). ORDER scope only — a project has no order
              checksum to gate an anonymous link, so we deliberately offer nothing here (deferred follow-up).
              Config-gated (default on); the backend still gates via chat.share.enabled + the mcp env. */}
          {scopeType === 'order' && scopeId && Environment.CHAT_SHARE_ENABLED && (
            <ShareFindingsButton scopeId={scopeId} />
          )}
        </div>

        {!llmAvailable && (
          <div className="alert alert-warning mb-2 py-2 text-sm">
            <span>
              The AI assistant is currently offline. You can still view existing answers and charts, but new
              questions can&apos;t be answered right now.
            </span>
          </div>
        )}

        {Environment.CHAT_DIGEST_ENABLED && scopeId && (
          <DigestCard scopeType={scopeType} scopeId={scopeId} />
        )}

        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="min-h-0 grow overflow-y-auto rounded-2xl border border-gray-200 bg-base-100 p-4"
        >
          {isEmpty ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <div className="text-gray-500">Try one of these to get started:</div>
              <div className="flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} className="btn btn-sm btn-outline" onClick={() => send(s)} disabled={loading || !llmAvailable}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            pathNodes.map((node, i) => {
              const m = node.message
              // Branch controls: siblings that share this node's on-path CHILD, and which one is active.
              const branchChildren = childrenOf(conversationNodes as NodeMap, node.id)
              const activeChildId = pathNodes[i + 1]?.id
              return (
                <ChatBubble
                  key={node.id}
                  nodeId={node.id}
                  message={m}
                  scopeType={scopeType}
                  scopeId={scopeId}
                  branchChildren={branchChildren}
                  activeChildId={activeChildId}
                  onBranchHere={() => dispatch(chatSlice.actions.setCurrentNode({ nodeId: node.id }))}
                  onSwitchBranch={(childId) =>
                    dispatch(
                      chatSlice.actions.setCurrentNode({
                        nodeId: deepestLeaf(conversationNodes as NodeMap, childId)
                      })
                    )
                  }
                  onPinContext={() => dispatch(chatSlice.actions.addGlobalContext({ text: m.content }))}
                  canRetry={m.role === 'assistant' && m.isError === true && !loading && llmAvailable}
                  onRetry={() => {
                    const q = i > 0 && pathNodes[i - 1].message.role === 'user' ? pathNodes[i - 1].message.content : ''
                    if (q) retry(i, q)
                  }}
                />
              )
            })
          )}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          {/* Always-on "current lens" indicator: the blast source + knobs the MOST RECENT query resolved to.
              Reuses the same badge look + formatProvenance helper as the per-chart ProvenanceChip. Renders
              nothing until a turn has resolved a lens (formatProvenance returns '' for an empty/undefined lens). */}
          {currentLensText ? (
            <span
              className="badge badge-ghost badge-sm shrink-0 font-normal"
              title="Reflects the most recent query's taxonomy lens. Older charts may show an earlier lens."
              aria-label={`Current taxonomy lens: ${currentLensText}`}
            >
              Current: {currentLensText}
            </span>
          ) : (
            <span />
          )}
          <span
            className="tooltip tooltip-left"
            data-tip="Show inline citations linking each fact in the answer to the evidence it came from"
          >
            <label className="label cursor-pointer gap-2 py-0">
              <span className="label-text text-xs opacity-70">Cite sources</span>
              <input
                type="checkbox"
                className="toggle toggle-xs"
                checked={citeSources}
                onChange={(e) => dispatch(chatSlice.actions.setCiteSources(e.target.checked))}
              />
            </label>
          </span>
        </div>

        {/* W-1: workspace-global context — user-pinned facts sent to the agent every turn (bridges branches). */}
        <GlobalContextBar />

        <div className="mt-2 flex items-end gap-2">
          <textarea
            ref={composerRef}
            className="textarea textarea-bordered grow resize-none"
            rows={2}
            placeholder={
              llmAvailable
                ? 'Ask a question…  (Enter to send, Shift+Enter for a new line)'
                : 'The AI assistant is offline right now…'
            }
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={loading || !llmAvailable}
          />
          {loading ? (
            <button className="btn btn-error" onClick={stop} title="Stop generating this answer">
              <StopIcon className="w-5" /> Stop
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => send(draft)}
              disabled={!draft.trim() || !llmAvailable}
            >
              <PaperAirplaneIcon className="w-5" /> Send
            </button>
          )}
        </div>
      </div>

      {/* Right column: the W-2 unified Workspace — one scrollable list of item-cards (charts + report + notes),
          each wrapping the existing rendering, with pin / stage / maximize + editable title/subtitle. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-base-200/30 lg:flex-[3]">
        <WorkspacePanel />
      </div>
    </div>
  )
}

/**
 * Workspace-global CONTEXT bar (W-1). User-managed facts that persist across conversation branches and are sent
 * to the agent every turn (appended to `vizContext` in the epic). Collapsed to a compact summary by default;
 * expands to the fact chips (each removable) plus a small add-input. Facts also arrive via a message's
 * "Remember this" affordance. Agent-driven promotion (a tool the model can call) is a later follow-up.
 */
function GlobalContextBar() {
  const dispatch = useDispatch()
  const facts = useSelector(selectGlobalContext)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')

  const add = () => {
    const text = draft.trim()
    if (!text) return
    dispatch(chatSlice.actions.addGlobalContext({ text }))
    setDraft('')
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        className="btn btn-ghost btn-xs gap-1"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title="Facts the assistant should keep in mind across the whole conversation, including branches"
      >
        <MapPinIcon className="w-4" />
        Workspace context
        {facts.length > 0 && <span className="badge badge-primary badge-xs">{facts.length}</span>}
      </button>
      {open && (
        <div className="mt-1 rounded-xl border border-gray-200 bg-base-100 p-2">
          {facts.length === 0 ? (
            <p className="px-1 py-0.5 text-xs opacity-60">
              No workspace facts yet. Add a fact the assistant should remember for every question (e.g. “these
              samples are all from Site A”). It applies across all branches.
            </p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {facts.map((fact, i) => (
                <li key={`${i}:${fact}`} className="badge badge-outline gap-1 py-3 pl-2 pr-1 font-normal">
                  <span className="max-w-xs truncate" title={fact}>
                    {fact}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs h-4 min-h-0 w-4 p-0"
                    onClick={() => dispatch(chatSlice.actions.removeGlobalContext({ index: i }))}
                    aria-label={`Remove workspace fact: ${fact}`}
                    title="Remove"
                  >
                    <XMarkIcon className="w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex items-center gap-2">
            <input
              type="text"
              className="input input-bordered input-xs grow"
              placeholder="Add a fact the assistant should always keep in mind…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  add()
                }
              }}
            />
            <button type="button" className="btn btn-primary btn-xs gap-1" onClick={add} disabled={!draft.trim()}>
              <PlusIcon className="w-3.5" /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** Width (px) of the glossary tooltip layer, used to clamp it inside the viewport. */
const GLOSSARY_POPOVER_WIDTH = 260

/**
 * Inline glossary-term affordance for the OVERVIEW digest (frontend task #61). react-markdown renders one of
 * these for every `<gterm>` element {@link remarkGlossary} emitted — i.e. the FIRST mention of each curated
 * domain term (SPIN, reads, OTU, IUCN categories, …). The term text stays inline with a subtle dotted
 * underline + a small ⓘ; hovering, focusing, or clicking reveals its one-line definition so the digest reads
 * clean and definitions are on-demand rather than inlined.
 *
 * Accessibility: the affordance is a real `<button>` (tab-focusable), opens on focus as well as hover/click,
 * carries an `aria-label` with the full definition and `aria-describedby`/`aria-expanded`, and closes on
 * Escape. Theme-aware via daisyUI semantic classes (base-100/base-content/base-300), so it works in light and
 * dark. The definition is portaled to `document.body` with fixed positioning (clamped + flipped) — the digest
 * body is a `max-h-72 overflow-y-auto` box, so an absolutely-positioned layer would clip; this mirrors the
 * {@link CiteChip} popover approach and closes on outside-click / scroll / resize too.
 */
function GlossaryTerm({ node, children }: ExtraProps & { children?: React.ReactNode }) {
  const props = node?.properties ?? {}
  const label = typeof props.label === 'string' ? props.label : ''
  const def = typeof props.def === 'string' ? props.def : ''
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; placeAbove: boolean } | undefined>(undefined)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const popId = useId()

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        anchorRef.current?.blur()
      }
    }
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (anchorRef.current?.contains(t) || popRef.current?.contains(t)) return
      setOpen(false)
    }
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open])

  const place = () => {
    const el = anchorRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const placeAbove = r.top > window.innerHeight - r.bottom
    const maxLeft = window.innerWidth - GLOSSARY_POPOVER_WIDTH - 8
    const left = Math.max(8, Math.min(r.left, maxLeft))
    setPos({ top: placeAbove ? r.top - 4 : r.bottom + 4, left, placeAbove })
    setOpen(true)
  }

  return (
    <span className="whitespace-nowrap">
      <button
        ref={anchorRef}
        type="button"
        className="cursor-help border-0 bg-transparent p-0 underline decoration-primary/50 decoration-dotted decoration-1 underline-offset-2"
        style={{ font: 'inherit', color: 'inherit' }}
        aria-label={def ? `${label}: ${def}` : label}
        aria-expanded={open}
        aria-describedby={open ? popId : undefined}
        onMouseEnter={place}
        onMouseLeave={() => {
          if (document.activeElement !== anchorRef.current) setOpen(false)
        }}
        onFocus={place}
        onBlur={() => setOpen(false)}
        onClick={() => (open ? setOpen(false) : place())}
      >
        {children}
        <sup aria-hidden="true" className="ml-0.5 text-[0.7em] text-primary/70 no-underline">
          ⓘ
        </sup>
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            id={popId}
            role="tooltip"
            className="fixed z-[100] rounded-lg border border-base-300 bg-base-100 p-2 text-xs leading-snug text-base-content shadow-lg"
            style={{
              top: pos.top,
              left: pos.left,
              width: GLOSSARY_POPOVER_WIDTH,
              transform: pos.placeAbove ? 'translateY(-100%)' : undefined
            }}
          >
            <div className="mb-0.5 font-semibold">{label}</div>
            <div className="opacity-80">{def}</div>
          </div>,
          document.body
        )}
    </span>
  )
}

// Digest markdown wiring, built once: remark-gfm + the glossary decorator, and a `components` map wiring the
// custom `<gterm>` carrier to GlossaryTerm. `gterm` is not an intrinsic tag so the map is cast (same shape the
// intrinsic `cite` mapping in ChatBubble takes, minus the type escape). The glossary plugin's first-occurrence
// `seen` set is created fresh per transform run inside the plugin, so sharing this instance across renders is safe.
const DIGEST_REMARK_PLUGINS = [remarkGfm, remarkGlossary()]
const DIGEST_MD_COMPONENTS = {
  gterm: (props: ExtraProps & { children?: React.ReactNode }) => (
    <GlossaryTerm node={props.node}>{props.children}</GlossaryTerm>
  )
} as unknown as Components

/**
 * Fallback SHORT headline derived from the breakdown markdown when the backend didn't supply `summary` (a parse
 * miss on the old digest shape). Takes the first non-header paragraph, strips inline markdown, and caps the
 * length — just enough for a readable one-liner so the card never shows a marker-littered or empty headline.
 */
function headlineFromMarkdown(md: string): string {
  const firstBlock = md
    .trim()
    .split(/\n\s*\n/)
    .find((p) => p.split('\n').some((l) => l.trim() !== '' && !l.trim().startsWith('#')))
  const text = (firstBlock ?? md)
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '' && !l.startsWith('#'))
    .join(' ')
    .replace(/[*_`>]/g, '')
    .replace(/^[-•]\s*/, '')
    .trim()
  return text.slice(0, 400)
}

/**
 * Passive "read this first" scope OVERVIEW card (D-digest, roadmap #4). On mount (and whenever the scope
 * changes) it fetches `GET /api/chat/digest`, which the backend composes ONCE server-side by reusing the chat
 * engine + tools + honesty layer, then caches. The FIRST fetch for a scope is slow (up to ~60s — a full agent
 * loop), so we show a non-blocking "Composing your overview…" spinner rather than a blocked page; later fetches
 * return instantly.
 *
 * Layout: the DEFAULT view is deliberately SHORT — a one-paragraph `summary` headline plus 3–5
 * project-specific `suggestedQuestions` rendered as clickable chips that seed the composer (via `seedComposer`,
 * the same mechanism a chart's "Ask about this" uses — fills the composer, doesn't auto-send). The fuller
 * BREAKDOWN markdown lives behind a "Show full overview" collapse (default COLLAPSED) whose body scrolls
 * internally (max-height) so it can't grow the page; that markdown keeps the glossary tooltip rendering. On a
 * parse miss the summary falls back to the breakdown's first lines and the chips to the generic starters. On
 * error (LLM offline → clean 503, config-disabled → 404, network) it shows a compact notice + Retry, so a
 * transient failure isn't just a blank — it must never break the chat page.
 */
/** LocalStorage-remembered, per-scope-type: has the user opted into auto-loading the overview? */
const OVERVIEW_AUTOLOAD_KEY = (scopeType: string): string => `chat.overview.autoload.${scopeType}`
function readOverviewAutoload(scopeType: string): boolean {
  try {
    return localStorage.getItem(OVERVIEW_AUTOLOAD_KEY(scopeType)) === '1'
  } catch {
    return false
  }
}
function writeOverviewAutoload(scopeType: string, on: boolean): void {
  try {
    localStorage.setItem(OVERVIEW_AUTOLOAD_KEY(scopeType), on ? '1' : '0')
  } catch {
    // ignore storage failures (private mode etc.) — the overview just won't be remembered
  }
}

function DigestCard({ scopeType, scopeId }: { scopeType: 'order' | 'project'; scopeId: string }) {
  const dispatch = useDispatch()
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [digest, setDigest] = useState<ChatDigest | undefined>(undefined)
  // Lazy + remembered: the overview auto-loads (and its first compose is a ~60s agent loop) ONLY if the user
  // opted in before. Default OFF so a cold open never blocks on something you didn't ask for — you get a
  // one-tap "Load overview" instead. Toggling persists the choice per scope-type; "Hide" opts back out.
  const [wantOverview, setWantOverview] = useState(() => readOverviewAutoload(scopeType))
  // The fuller breakdown starts COLLAPSED — the short summary + chips are the default view.
  const [showBreakdown, setShowBreakdown] = useState(false)
  // Bumping this re-runs the fetch effect — the Retry affordance on the error state.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!wantOverview) return
    let cancelled = false
    setStatus('loading')
    setDigest(undefined)
    setShowBreakdown(false)
    getChatDigest(scopeType, scopeId)
      .then((d) => {
        if (cancelled) return
        setDigest(d)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [scopeType, scopeId, reloadKey, wantOverview])

  const toggleOverview = (): void =>
    setWantOverview((w) => {
      const next = !w
      writeOverviewAutoload(scopeType, next)
      return next
    })

  // Don't break the page on failure, but don't silently vanish either: show a compact notice + Retry.
  if (wantOverview && status === 'error') {
    return (
      <div className="mb-3 flex items-center justify-between gap-2 rounded-2xl border border-warning/30 bg-warning/5 p-3">
        <div className="flex items-center gap-2 text-sm opacity-80">
          <SparklesIcon className="w-4 text-warning" />
          Couldn&apos;t load the overview right now.
        </div>
        <button type="button" className="btn btn-ghost btn-xs gap-1" onClick={() => setReloadKey((k) => k + 1)}>
          <ArrowPathIcon className="w-4" /> Retry
        </button>
      </div>
    )
  }

  // Short headline (fallback to the breakdown's first lines if the backend didn't parse out a summary). The
  // summary is contractually PLAIN TEXT, but the model occasionally leaks a markdown header (e.g. '**Overview**')
  // into it — and this line renders as-is (not through ReactMarkdown, unlike the full overview). Strip inline
  // markers + leading heading marks so the headline never shows raw asterisks/backticks (matches the
  // sanitisation headlineFromMarkdown already applies on the fallback path).
  const summary = (digest?.summary?.trim() || (digest?.markdown ? headlineFromMarkdown(digest.markdown) : ''))
    .replace(/[*_`>]/g, '')
    .replace(/^\s*#+\s*/gm, '')
    .trim()
  // Project-specific suggested questions; fall back to the generic starters on a parse miss / empty list.
  const questions = digest?.suggestedQuestions?.filter((q) => q.trim() !== '') ?? []
  const chips = questions.length > 0 ? questions : SUGGESTIONS

  return (
    <div className="mb-3 rounded-2xl border border-primary/30 bg-primary/5 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <SparklesIcon className="w-4 text-primary" />
          Overview — read this first
        </div>
        <button type="button" className="btn btn-ghost btn-xs" onClick={toggleOverview}>
          {wantOverview ? 'Hide' : 'Load overview'}
        </button>
      </div>
      {wantOverview && (
        <div className="mt-2">
          {status === 'loading' ? (
            <div className="flex items-center gap-2 text-sm opacity-70">
              <span className="loading loading-dots loading-sm" /> Composing your overview… this can take up to a
              minute the first time.
            </div>
          ) : (
            <>
              {summary !== '' && <p className="text-sm leading-relaxed">{summary}</p>}
              {chips.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {chips.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="btn btn-xs btn-outline btn-primary"
                      onClick={() => dispatch(chatSlice.actions.seedComposer({ text: q }))}
                      title="Ask this — fills the composer so you can review and send"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {digest?.markdown && (
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs gap-1"
                    aria-expanded={showBreakdown}
                    onClick={() => setShowBreakdown((s) => !s)}
                  >
                    {showBreakdown ? 'Hide full overview' : 'Show full overview'}
                  </button>
                  {showBreakdown && (
                    <div className="prose prose-sm mt-2 max-h-72 max-w-none overflow-y-auto">
                      <ReactMarkdown remarkPlugins={DIGEST_REMARK_PLUGINS} components={DIGEST_MD_COMPONENTS}>
                        {digest.markdown}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** Compact, locale-aware "saved 2 Aug, 14:30" label for a workspace summary row. */
function formatSavedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

/** localStorage key for the W-3b opt-in: when 'true', workspaces sync to the account (encrypted at rest). */
const WORKSPACE_SYNC_KEY = 'spcfy-chat-workspace-sync'

function readWorkspaceSync(): boolean {
  try {
    return window.localStorage.getItem(WORKSPACE_SYNC_KEY) === 'true'
  } catch {
    return false
  }
}

function writeWorkspaceSync(on: boolean): void {
  try {
    window.localStorage.setItem(WORKSPACE_SYNC_KEY, on ? 'true' : 'false')
  } catch {
    // Private-mode / storage-disabled: the toggle just won't persist across reloads. Non-fatal.
  }
}

/**
 * W-0/W-3b workspace persistence controls. Two small header affordances plus an opt-in sync toggle:
 *  - "Save workspace" — snapshots the current `chat` state (conversation + rendered graphs + report blocks,
 *    minus volatile fields) into a {@link Workspace} and persists it via the ACTIVE store. Prompts for a title.
 *  - "My workspaces" — lists this scope's saved workspaces (newest-first); each row Resumes (hydrate the slice
 *    + re-render the report) or Deletes. Resuming replaces a non-empty conversation behind a confirm.
 *  - "Sync to my account" — the W-3b opt-in (default OFF, browser-only). When ON, the controls use
 *    the {@link ServerWorkspaceStore} (server encrypts at rest via CryptoService — server-held key, not E2E);
 *    else the browser
 *    {@link IndexedDbWorkspaceStore}. The choice persists in localStorage. When server-side crypto is off the
 *    endpoints 404, so a failed server `list` DEGRADES gracefully back to the browser store with a note.
 */
function WorkspaceControls({ scopeKey }: { scopeKey: string }) {
  const dispatch = useDispatch()
  const chat = useSelector((state) => state.chat)
  const hasConversation = chat.messages.length > 0

  const [menuOpen, setMenuOpen] = useState(false)
  const [summaries, setSummaries] = useState<WorkspaceSummary[]>([])
  const [listLoading, setListLoading] = useState(false)
  // W-3b opt-in: sync workspaces to the account (encrypted server-side) vs browser-only. Persisted in localStorage.
  const [syncEnabled, setSyncEnabled] = useState<boolean>(readWorkspaceSync)
  // Set when the server endpoints are unreachable (e.g. crypto off server-side → 404): we fall back to browser-only.
  const [serverUnavailable, setServerUnavailable] = useState(false)
  // Transient "Saved ✓" / error confirmation shown next to the buttons.
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'error'; text: string } | undefined>(undefined)
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // daisyUI modal state (replaces native window.prompt/window.confirm): the Save name-entry dialog, and the
  // "resuming replaces the current conversation" confirm (holds the pending workspace id while open).
  const [saveName, setSaveName] = useState<string | undefined>(undefined)
  const [pendingResumeId, setPendingResumeId] = useState<string | undefined>(undefined)

  // A single ServerWorkspaceStore instance while sync is on. The browser store is the app-wide default singleton.
  const serverStore = useMemo(() => new ServerWorkspaceStore(), [])
  const useServer = syncEnabled && !serverUnavailable
  const store = useServer ? serverStore : defaultWorkspaceStore

  const flash = (kind: 'ok' | 'error', text: string) => {
    setFeedback({ kind, text })
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current)
    feedbackTimer.current = setTimeout(() => setFeedback(undefined), 4000)
  }
  useEffect(() => () => feedbackTimer.current && clearTimeout(feedbackTimer.current), [])

  const toggleSync = async (on: boolean) => {
    setSyncEnabled(on)
    setServerUnavailable(false)
    writeWorkspaceSync(on)
    if (menuOpen) await refreshList(on)
  }

  // `forceServer` lets the toggle handler list against the just-selected store before state has re-rendered.
  const refreshList = async (forceServer?: boolean) => {
    const active = (forceServer ?? useServer) ? serverStore : defaultWorkspaceStore
    setListLoading(true)
    try {
      setSummaries(await active.list(scopeKey))
    } catch {
      // Server unreachable (crypto off / offline): fall back to the browser store's list and note it.
      if (active === serverStore) {
        setServerUnavailable(true)
        flash('error', 'Account sync unavailable — showing browser-only workspaces')
        try {
          setSummaries(await defaultWorkspaceStore.list(scopeKey))
        } catch {
          setSummaries([])
        }
      } else {
        setSummaries([])
      }
    } finally {
      setListLoading(false)
    }
  }

  // Default workspace title = the first user question (capped), used to pre-fill the Save dialog.
  const defaultSaveTitle = () => {
    const firstUser = chat.messages.find((m) => m.role === 'user')?.content?.trim() ?? ''
    return (firstUser || 'Untitled workspace').slice(0, 60)
  }

  // Open the daisyUI Save dialog pre-filled with the default title (replaces the native window.prompt).
  const onSave = () => {
    if (!hasConversation) return
    setSaveName(defaultSaveTitle())
  }

  // Commit the Save dialog: persist the workspace under the entered (or default) title, then close.
  const confirmSave = async () => {
    const defaultTitle = defaultSaveTitle()
    const title = (saveName ?? '').trim() || defaultTitle
    setSaveName(undefined)
    try {
      await store.save(snapshotWorkspace(chat, title))
      flash('ok', useServer ? 'Workspace saved to your account' : 'Workspace saved')
      if (menuOpen) await refreshList()
    } catch {
      // A server save that fails (crypto off / offline) drops back to browser-only so the save isn't lost.
      if (useServer) {
        setServerUnavailable(true)
        try {
          await defaultWorkspaceStore.save(snapshotWorkspace(chat, title))
          flash('error', 'Account sync unavailable — saved to this browser instead')
          if (menuOpen) await refreshList(false)
        } catch {
          flash('error', 'Could not save workspace')
        }
      } else {
        flash('error', 'Could not save workspace')
      }
    }
  }

  const openMenu = async () => {
    setMenuOpen(true)
    await refreshList()
  }

  // Resuming into a non-empty conversation is destructive → route through a daisyUI confirm dialog (replaces the
  // native window.confirm). An empty conversation resumes straight away.
  const requestResume = (id: string) => {
    if (hasConversation) {
      setPendingResumeId(id)
      return
    }
    void doResume(id)
  }

  const doResume = async (id: string) => {
    setPendingResumeId(undefined)
    try {
      const ws = await store.get(id)
      if (!ws) {
        flash('error', 'Workspace not found')
        return
      }
      dispatch(chatSlice.actions.hydrateWorkspace(hydrateFromWorkspace(ws)))
      // Re-render the report from its blocks: applyReportEdits(REPLACE) is what RenderReportEpic reacts to.
      // Charts (openGraphs) rehydrated directly in the reducer.
      if (ws.view.report.blocks.length > 0) {
        dispatch(
          chatSlice.actions.applyReportEdits([
            { op: 'REPLACE', blocks: ws.view.report.blocks, title: ws.view.report.title }
          ])
        )
      }
      setMenuOpen(false)
    } catch {
      flash('error', 'Could not resume workspace')
    }
  }

  const onDelete = async (id: string) => {
    try {
      await store.delete(id)
      setSummaries((s) => s.filter((w) => w.id !== id))
    } catch {
      flash('error', 'Could not delete workspace')
    }
  }

  return (
    <div className="relative flex shrink-0 items-center gap-2">
      {feedback && (
        <span
          className={`flex items-center gap-1 text-xs ${feedback.kind === 'ok' ? 'text-success' : 'text-error'}`}
          role="status"
        >
          {feedback.kind === 'ok' && <CheckCircleIcon className="w-4" />}
          {feedback.text}
        </span>
      )}
      <button
        type="button"
        className="btn btn-ghost btn-sm gap-1"
        onClick={onSave}
        disabled={!hasConversation}
        title="Save this chat (incl. its graphs and report) to resume later — stored in this browser only"
      >
        <BookmarkIcon className="w-4" /> Save workspace
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-sm gap-1"
        onClick={() => (menuOpen ? setMenuOpen(false) : void openMenu())}
        aria-expanded={menuOpen}
        title="Resume a saved workspace for this dataset"
      >
        <FolderOpenIcon className="w-4" /> My workspaces
      </button>

      {menuOpen && (
        <>
          {/* Click-away backdrop. */}
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 top-full z-50 mt-1 w-80 rounded-xl border border-gray-200 bg-base-100 p-2 shadow-lg">
            {/* W-3b opt-in: sync workspaces to the account (server encrypts at rest) vs browser-only. */}
            <label className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-base-200">
              <input
                type="checkbox"
                className="toggle toggle-sm mt-0.5"
                checked={syncEnabled}
                onChange={(e) => void toggleSync(e.target.checked)}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium">Sync to my account</span>
                <span className="block text-xs opacity-60">
                  {syncEnabled
                    ? serverUnavailable
                      ? 'Account sync unavailable — using this browser only.'
                      : 'Stored encrypted at rest (we hold the key). Available on any device.'
                    : 'Off — workspaces stay in this browser only.'}
                </span>
              </span>
            </label>
            <div className="my-1 border-t border-gray-200" />
            <div className="px-2 py-1 text-xs font-semibold opacity-60">Saved for this dataset</div>
            {listLoading ? (
              <div className="flex items-center gap-2 px-2 py-3 text-sm opacity-70">
                <span className="loading loading-dots loading-sm" /> Loading…
              </div>
            ) : summaries.length === 0 ? (
              <div className="px-2 py-3 text-sm opacity-70">No saved workspaces for this dataset yet.</div>
            ) : (
              <ul className="max-h-80 overflow-y-auto">
                {summaries.map((w) => (
                  <li key={w.id} className="flex items-center gap-1 rounded-lg px-2 py-1.5 hover:bg-base-200">
                    <div className="min-w-0 grow">
                      <div className="truncate text-sm font-medium" title={w.title}>
                        {w.title}
                      </div>
                      <div className="text-xs opacity-60">{formatSavedAt(w.savedAt)}</div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs gap-1"
                      onClick={() => requestResume(w.id)}
                      title="Resume this workspace"
                    >
                      <ArrowUturnLeftIcon className="w-4" /> Resume
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs text-error"
                      onClick={() => void onDelete(w.id)}
                      title="Delete this workspace"
                      aria-label={`Delete workspace ${w.title}`}
                    >
                      <TrashIcon className="w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {/* Save-name dialog (replaces window.prompt). Enter commits, Escape/Cancel dismisses. */}
      {saveName !== undefined && (
        <div className="modal modal-open">
          <div className="modal-box w-full max-w-sm">
            <h3 className="text-base font-bold">Name this workspace</h3>
            <p className="mt-1 text-sm opacity-70">
              Save this chat — including its graphs and report — so you can resume it later.
            </p>
            <input
              type="text"
              autoFocus
              className="input input-bordered mt-3 w-full"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void confirmSave()
                }
              }}
              aria-label="Workspace name"
            />
            <div className="modal-action">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSaveName(undefined)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => void confirmSave()}>
                Save
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setSaveName(undefined)} />
        </div>
      )}

      {/* Resume-overwrite confirm dialog (replaces window.confirm). */}
      {pendingResumeId !== undefined && (
        <div className="modal modal-open">
          <div className="modal-box w-full max-w-sm">
            <h3 className="text-base font-bold">Replace current conversation?</h3>
            <p className="mt-1 text-sm opacity-70">
              Resuming this workspace replaces the conversation you have open now. Continue?
            </p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setPendingResumeId(undefined)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => void doResume(pendingResumeId)}
              >
                Resume
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setPendingResumeId(undefined)} />
        </div>
      )}
    </div>
  )
}

/**
 * No-login "Share findings" snapshot button (D-share, roadmap #6; ORDER scope only). On click it mints a
 * snapshot via `POST /api/chat/share` (the backend composes/reuses the digest AS THE LOGGED-IN USER, freezes
 * it to self-contained HTML, and returns a checksum-gated anonymous URL — mirroring the order report share
 * link), then copies the URL to the clipboard and surfaces it. The first mint can be slow if the digest is
 * not yet warm (it composes the overview), so we show a "Creating link…" state. The URL is also shown in a
 * read-only field as a fallback when the clipboard API is blocked.
 */
function ShareFindingsButton({ scopeId }: { scopeId: string }) {
  const [state, setState] = useState<'idle' | 'working' | 'copied' | 'error'>('idle')
  const [url, setUrl] = useState('')

  const onClick = async () => {
    setState('working')
    try {
      const shareUrl = await shareFindings('order', scopeId)
      setUrl(shareUrl)
      try {
        await navigator.clipboard.writeText(shareUrl)
      } catch {
        // Clipboard blocked (e.g. insecure context) — still show the URL for manual copy.
      }
      setState('copied')
    } catch {
      setState('error')
    }
  }

  return (
    <div className="mt-2 flex flex-col gap-1">
      <button
        type="button"
        className="btn btn-outline btn-sm w-fit"
        onClick={onClick}
        disabled={state === 'working'}
        title="Create a no-login link to a read-only findings snapshot anyone can open"
      >
        <LinkIcon className="w-4" />
        {state === 'working' ? 'Creating link…' : state === 'copied' ? 'Link copied' : 'Share findings'}
      </button>
      {state === 'copied' && url && (
        <input
          type="text"
          readOnly
          className="input input-bordered input-xs w-full max-w-xl"
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          aria-label="Shareable findings link"
        />
      )}
      {state === 'error' && (
        <span className="text-xs text-error">Could not create a share link. Please try again.</span>
      )}
    </div>
  )
}

// Friendly, present-tense labels for the live "what is the agent doing now" line during streaming.
const STEP_LABELS: Record<string, string> = {
  describeScope: 'Inspecting the dataset…',
  scopeInfo: 'Reading dataset details…',
  traitCatalog: 'Looking up traits…',
  searchOtus: 'Searching OTUs…',
  otuDetail: 'Fetching OTU details…',
  otusByTrait: 'Finding taxa by trait…',
  diversityMetrics: 'Computing diversity…',
  sampleQuality: 'Checking sample quality…',
  spinAlgorithms: 'Listing SPIN algorithms…',
  gbifOccurrence: 'Querying GBIF…',
  iucnStatus: 'Checking IUCN status…',
  ncbiTaxonomy: 'Looking up NCBI taxonomy…',
  boldRecords: 'Checking BOLD records…',
  speciesInfo: 'Looking up species info…',
  renderChart: 'Preparing a chart…',
  updateChart: 'Updating a chart…',
  focusChart: 'Pointing at a chart…'
}

function stepLabel(tool: string): string {
  return STEP_LABELS[tool] ?? `Running ${tool}…`
}

/** Live "thinking…" → latest-tool progress line shown inside a streaming assistant bubble. */
function StreamingIndicator({ message }: { message: IChatMessage }) {
  const steps = message.steps ?? []
  const label = steps.length > 0 ? stepLabel(steps[steps.length - 1].tool) : 'thinking…'
  return (
    <div className="mt-1 flex items-center gap-2 text-xs opacity-70">
      <span className="loading loading-dots loading-xs" /> {label}
    </div>
  )
}

// Human-facing group headings for the deterministic "Evidence used" panel (Plan A — Epic #7, Tier 1). Order
// matters: reports (the provenance anchor) first, then the OTUs and samples the answer leaned on.
const EVIDENCE_KINDS: { kind: string; label: string }[] = [
  { kind: 'report', label: 'Reports' },
  { kind: 'otu', label: 'OTUs' },
  { kind: 'sample', label: 'Samples' },
  { kind: 'citation', label: 'Literature' }
]

/** A couple of the ref's context fields as a compact "Reads: 1234, Grade: 98" tail; empty when it has none. */
function formatEvidenceFields(fields: Record<string, unknown> | undefined): string {
  if (!fields) return ''
  return Object.entries(fields)
    .filter(([k, v]) => v !== null && v !== undefined && v !== '' && !isHiddenField(k, v))
    .slice(0, 2)
    .map(([k, v]) => `${fieldLabel(k)}: ${String(v)}`)
    .join(', ')
}

/**
 * Deterministic, hallucination-immune "Evidence used" panel (Plan A — Epic #7, Tier 1). Collapsed by default;
 * expands to the DISTINCT reports/OTUs/samples the agent actually looked at this turn, grouped by kind. Built
 * server-side from real tool output (never model-typed), so it is safe to show verbatim. Assistant turns only.
 */
function EvidencePanel({
  evidence,
  scopeType,
  scopeId
}: {
  evidence: EvidenceRef[]
  scopeType: 'order' | 'project'
  scopeId: string
}) {
  // Which entry (if any) is expanded to its shared free-tier EvidenceView card. Click an entry to toggle.
  const [openId, setOpenId] = useState<string | undefined>(undefined)
  const groups = useMemo(
    () =>
      EVIDENCE_KINDS.map(({ kind, label }) => ({
        label,
        refs: evidence.filter((e) => e.kind === kind)
      })).filter((g) => g.refs.length > 0),
    [evidence]
  )

  return (
    <details className="text-xs opacity-70">
      <summary className="cursor-pointer">Evidence used ({evidence.length} sources)</summary>
      <div className="mt-1 flex flex-col gap-2">
        {groups.map((group) => (
          <div key={group.label}>
            <div className="font-semibold">
              {group.label} ({group.refs.length})
            </div>
            <ul className="mt-0.5 list-disc pl-4">
              {group.refs.map((ref) => {
                const fields = formatEvidenceFields(ref.fields)
                const isOpen = openId === ref.id
                return (
                  <li key={ref.id} className="break-words">
                    <button
                      type="button"
                      className="cursor-pointer text-left hover:underline"
                      aria-expanded={isOpen}
                      onClick={() => setOpenId(isOpen ? undefined : ref.id)}
                    >
                      {ref.label}
                      {fields && <span className="opacity-70"> — {fields}</span>}
                    </button>
                    {isOpen && (
                      <div className="mt-1">
                        <EvidenceView evidence={ref} scopeType={scopeType} scopeId={scopeId} />
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </details>
  )
}

// Approximate popover width (EvidenceView is `w-64` = 16rem = 256px); used to clamp it inside the viewport.
const CITE_POPOVER_WIDTH = 256

/**
 * Inline `[eN]` citation chip (Epic A #7 Tier 2). Rendered by react-markdown for each `<cite>` element that
 * {@link remarkCiteRefs} emitted (only resolvable markers get that far — invented ids are stripped in the
 * plugin, so `ref` is expected to exist; the null guard is belt-and-suspenders). A subtle superscript badge;
 * clicking reveals the shared {@link EvidenceView} in a popover.
 *
 * The popover is rendered through a portal to `document.body` with `position: fixed`, anchored to the chip via
 * its bounding rect. A daisyUI `dropdown` positions its content ABSOLUTELY inside the message bubble, so the
 * chat scroll container's `overflow-y-auto` (and the bubble's own overflow) clipped it near the top/edges.
 * Portaling escapes every clipping ancestor; the left edge is clamped into the viewport so it never introduces
 * horizontal scroll, and it flips above/below the chip depending on available space. It closes on outside
 * click, Escape, scroll, or resize (any of which would move the anchor out from under a fixed layer).
 */
function CiteChip({
  node,
  evidence,
  scopeType,
  scopeId
}: ExtraProps & { evidence: EvidenceRef[]; scopeType: 'order' | 'project'; scopeId: string }) {
  const raw = node?.properties?.evidenceId
  const id = typeof raw === 'string' ? raw : String(raw ?? '')
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; placeAbove: boolean } | undefined>(undefined)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (anchorRef.current?.contains(t) || popRef.current?.contains(t)) return
      setOpen(false)
    }
    // Capture-phase scroll so the chat container's inner scroll (not just window scroll) also dismisses it.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open])

  const ref = evidence.find((e) => e.id === id)
  if (!ref) return null // unresolvable — render nothing (the marker is already stripped upstream)

  const meta = evidenceKindMeta(ref.kind)
  const num = id.replace(/^e/, '')

  const toggle = () => {
    if (open) {
      setOpen(false)
      return
    }
    const el = anchorRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    // Prefer above (matches the old dropdown-top feel); flip below if there's more room there.
    const placeAbove = r.top > window.innerHeight - r.bottom
    const maxLeft = window.innerWidth - CITE_POPOVER_WIDTH - 8
    const left = Math.max(8, Math.min(r.left, maxLeft))
    setPos({ top: placeAbove ? r.top - 4 : r.bottom + 4, left, placeAbove })
    setOpen(true)
  }

  return (
    <span className="align-super">
      <button
        ref={anchorRef}
        type="button"
        className="badge badge-ghost badge-xs mx-0.5 cursor-pointer gap-0.5"
        title={ref.label}
        aria-expanded={open}
        onClick={toggle}
      >
        <span aria-hidden="true">{meta.icon}</span>
        {num}
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            className="fixed z-[100]"
            style={{
              top: pos.top,
              left: pos.left,
              transform: pos.placeAbove ? 'translateY(-100%)' : undefined
            }}
          >
            <EvidenceView evidence={ref} scopeType={scopeType} scopeId={scopeId} />
          </div>,
          document.body
        )}
    </span>
  )
}

/**
 * Server-rendered chart PNGs the agent produced for this turn, shown inline in the assistant bubble. Each
 * image is responsive (never overflows the bubble) with a short alt, and carries a small provenance chip
 * beneath it (same `badge badge-ghost badge-sm` look + {@link formatProvenance} helper as the per-chart
 * ProvenanceChip). Renders nothing when the turn produced no images (the field is optional).
 */
function RenderedImages({ images }: { images: RenderedImage[] }) {
  if (images.length === 0) return null
  return (
    <div className="mt-2 flex flex-col gap-2">
      {images.map((img, i) => {
        const provenance = formatProvenance(img.provenance)
        const dataUrl = `data:image/png;base64,${img.pngBase64}`
        return (
          <figure key={i} className="m-0">
            <img
              src={dataUrl}
              alt={`${prettyKind(img.chartKind)} chart`}
              className="h-auto max-w-full rounded-lg border border-gray-200"
            />
            <figcaption className="mt-1 flex items-center gap-2">
              {provenance && (
                <span
                  className="badge badge-ghost badge-sm font-normal"
                  title={`Computed under: ${provenance}`}
                  aria-label={`Taxonomy source: ${provenance}`}
                >
                  {provenance}
                </span>
              )}
              {/* These are already PNG data URLs — download the base64 directly for full fidelity (no re-raster). */}
              <a
                href={dataUrl}
                download={`${prettyKind(img.chartKind)}.png`}
                className="btn btn-ghost btn-xs gap-1"
                title="Export this chart as PNG"
                aria-label="Export this chart as PNG"
              >
                <ArrowDownTrayIcon className="w-4" />
                Export PNG
              </a>
            </figcaption>
          </figure>
        )
      })}
    </div>
  )
}

/**
 * Server-rendered, print-styled report documents the agent produced for this turn, shown inline in the
 * assistant bubble. Each report's `html` is a COMPLETE self-contained `<!doctype html>` document (escaped
 * server-side), so we isolate it in a sandboxed iframe via `srcDoc` — NOT `dangerouslySetInnerHTML` — for
 * style/DOM isolation plus a working `window.print()` (Save-as-PDF). The sandbox intentionally omits
 * `allow-scripts` (the doc is static); `allow-same-origin` lets us reach `contentWindow.print()` and
 * `allow-modals` lets the print dialog open. Renders nothing when the turn produced no reports.
 */
function RenderedReports({ reports }: { reports: RenderedReport[] }) {
  const frameRefs = useRef<Array<HTMLIFrameElement | null>>([])

  if (reports.length === 0) return null
  return (
    <div className="mt-2 flex flex-col gap-3">
      {reports.map((report, i) => {
        const openInNewTab = () => {
          const blob = new Blob([report.html], { type: 'text/html' })
          const url = URL.createObjectURL(blob)
          window.open(url, '_blank', 'noopener,noreferrer')
          // Revoke shortly after so the new tab has time to load the document.
          setTimeout(() => URL.revokeObjectURL(url), 60_000)
        }
        return (
          <figure key={i} className="m-0 rounded-lg border border-gray-200">
            <figcaption className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
              <span className="truncate text-sm font-medium not-italic">{report.title ?? 'Report'}</span>
              <span className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  className="btn btn-ghost btn-xs gap-1"
                  onClick={() => frameRefs.current[i]?.contentWindow?.print()}
                  title="Print or save this report as a PDF"
                >
                  <PrinterIcon className="h-4 w-4" />
                  Print / Save as PDF
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs gap-1"
                  onClick={openInNewTab}
                  title="Open this report in a new tab"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  Open
                </button>
              </span>
            </figcaption>
            <iframe
              ref={(el) => {
                frameRefs.current[i] = el
              }}
              srcDoc={report.html}
              sandbox="allow-same-origin allow-modals"
              title={report.title ?? `Report ${i + 1}`}
              className="block h-[520px] w-full rounded-b-lg bg-white"
            />
          </figure>
        )
      })}
    </div>
  )
}

/**
 * Inline notice (U2b) shown under an assistant turn when the agent tried to edit a PINNED (locked) workspace
 * item and couldn't — making the otherwise-silent W-2b pin guard visible. While the item is still pinned it
 * offers a one-click Unpin (which lets the user re-ask the edit); once unpinned the action drops away and only
 * the historical "left it unchanged" note remains.
 */
function PinRefusalNotice({ refusal }: { refusal: PinRefusal }) {
  const dispatch = useDispatch()
  const stillPinned = useSelector(selectPinnedItemIds).includes(refusal.itemId)
  return (
    <div className="alert alert-info mt-1 gap-2 py-2 text-xs">
      <LockClosedIcon className="w-4 shrink-0" />
      <span className="grow">{refusal.message}</span>
      {stillPinned && (
        <button
          type="button"
          className="btn btn-ghost btn-xs"
          onClick={() => dispatch(chatSlice.actions.togglePinItem({ id: refusal.itemId }))}
        >
          Unpin
        </button>
      )}
    </div>
  )
}

function ChatBubble({
  nodeId,
  message,
  scopeType,
  scopeId,
  branchChildren,
  activeChildId,
  onBranchHere,
  onSwitchBranch,
  onPinContext,
  canRetry,
  onRetry
}: {
  /** This message's tree node id (W-1) — the anchor for branch/pin affordances. */
  nodeId: string
  message: IChatMessage
  scopeType: 'order' | 'project'
  scopeId: string
  /** This node's children (W-1). >1 = a fork: the branch switcher appears. */
  branchChildren: ChatNode[]
  /** Which child is on the active path (the branch currently shown), if any. */
  activeChildId?: string
  /** Point the conversation at this node so the next turn opens a new sibling branch here. */
  onBranchHere: () => void
  /** Switch the active path to a sibling branch (by child node id). */
  onSwitchBranch: (childId: string) => void
  /** Pin this message's text as a workspace-global fact. */
  onPinContext: () => void
  /** Show a Retry control for this (failed) turn. */
  canRetry?: boolean
  /** Re-send the paired user question for this turn. */
  onRetry?: () => void
}) {
  const dispatch = useDispatch()
  const isUser = message.role === 'user'
  // Inline-citation wiring (Tier 2): resolve `[eN]` markers against THIS turn's evidence ids in a remark
  // plugin (invented ids stripped), and map the resulting `<cite>` carrier element to the chip component.
  const evidence = useMemo(() => message.evidence ?? [], [message.evidence])
  const remarkPlugins = useMemo(
    () => [remarkGfm, remarkCiteRefs(new Set(evidence.map((e) => e.id)))],
    [evidence]
  )
  const mdComponents = useMemo<Components>(
    () => ({
      cite: (props) => (
        <CiteChip node={props.node} evidence={evidence} scopeType={scopeType} scopeId={scopeId} />
      )
    }),
    [evidence, scopeType, scopeId]
  )
  // Unique charts this answer opened/changed — clickable chips that focus the panel on the canvas.
  const chips = useMemo(() => {
    const seen = new Set<string>()
    return (message.directives ?? []).filter((d) => {
      const key = directiveKey(d)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [message.directives])

  // W-1 branch controls. The switcher appears at a fork (this node has >1 child); "branch here" appears on a
  // finished assistant answer (its next turn opens a sibling exploration). Pinning is available on any message.
  const activeChildIndex = branchChildren.findIndex((c) => c.id === activeChildId)
  const showSwitcher = branchChildren.length > 1 && activeChildIndex >= 0
  const canBranch = !isUser && !message.streaming && !message.isError
  const switchTo = (delta: number) => {
    const next = (activeChildIndex + delta + branchChildren.length) % branchChildren.length
    onSwitchBranch(branchChildren[next].id)
  }

  return (
    <>
      <div className={`chat ${isUser ? 'chat-end' : 'chat-start'}`}>
        <div className={`chat-bubble ${isUser ? 'chat-bubble-primary' : message.isError ? 'chat-bubble-error' : ''}`}>
          {isUser ? (
            <span className="whitespace-pre-wrap">{message.content}</span>
          ) : (
            <div className="prose prose-sm max-w-none">
              {message.content && (
                <ReactMarkdown remarkPlugins={remarkPlugins} components={mdComponents}>
                  {message.content}
                </ReactMarkdown>
              )}
              {message.renderedImages && message.renderedImages.length > 0 && (
                <RenderedImages images={message.renderedImages} />
              )}
              {message.renderedReports && message.renderedReports.length > 0 && (
                <RenderedReports reports={message.renderedReports} />
              )}
              {message.streaming && <StreamingIndicator message={message} />}
              {message.stopped && (
                <div className="mt-1 text-xs italic opacity-60">
                  {message.content ? 'Stopped — partial answer above.' : 'Stopped.'}
                </div>
              )}
              {message.pinRefusals && message.pinRefusals.length > 0 && (
                <div className="mt-2 flex flex-col gap-1">
                  {message.pinRefusals.map((refusal, i) => (
                    <PinRefusalNotice key={i} refusal={refusal} />
                  ))}
                </div>
              )}
            </div>
          )}
          {message.steps && message.steps.length > 0 && (
            <details className="mt-2 text-xs opacity-70">
              <summary className="cursor-pointer">Tools used ({message.steps.length})</summary>
              <ul className="mt-1 list-disc pl-4">
                {message.steps.map((step, i) => (
                  <li key={i}>
                    <code>{step.tool}</code>
                    {step.args && Object.keys(step.args).length > 0 && ` (${JSON.stringify(step.args)})`}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
      {/* W-1 branch + pin controls row. Subtle, and entirely absent on a plain linear message (no fork, and
          "branch here"/"pin" are opt-in hover-weight affordances) so a branch-free chat looks like before. */}
      <div className={`mb-2 flex items-center gap-1 ${isUser ? 'justify-end pr-1' : 'ml-1'}`}>
        {showSwitcher && (
          <span
            className="inline-flex items-center gap-0.5 rounded-md bg-base-200 px-1 text-xs"
            title="Switch conversation branch — other answers explored from this point"
            aria-label={`Branch ${activeChildIndex + 1} of ${branchChildren.length} from this message`}
          >
            <ArrowsRightLeftIcon className="w-3 shrink-0 opacity-50" aria-hidden="true" />
            <button
              type="button"
              className="btn btn-ghost btn-xs h-5 min-h-0 w-5 p-0"
              onClick={() => switchTo(-1)}
              aria-label="Previous branch"
              title="Switch conversation branch"
            >
              <ChevronLeftIcon className="w-3.5" />
            </button>
            <span className="tabular-nums opacity-70">
              {activeChildIndex + 1}/{branchChildren.length}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-xs h-5 min-h-0 w-5 p-0"
              onClick={() => switchTo(1)}
              aria-label="Next branch"
            >
              <ChevronRightIcon className="w-3.5" />
            </button>
          </span>
        )}
        {canBranch && (
          <button
            type="button"
            className="btn btn-ghost btn-xs gap-1 opacity-60 hover:opacity-100"
            onClick={onBranchHere}
            title="Explore an aside from here — your next question starts a new branch; the main line is kept"
          >
            <ArrowsRightLeftIcon className="w-3.5" /> Branch here
          </button>
        )}
        {message.content.trim() !== '' && !message.streaming && (
          <button
            type="button"
            className="btn btn-ghost btn-xs gap-1 opacity-60 hover:opacity-100"
            onClick={onPinContext}
            data-node={nodeId}
            title="Remember this — add it to the workspace context so the assistant keeps it in mind across all branches"
            aria-label="Remember this in the workspace context"
          >
            <MapPinIcon className="w-3.5" /> Remember this
          </button>
        )}
      </div>
      {!isUser && canRetry && onRetry && (
        <div className="mb-2 ml-1">
          <button type="button" className="btn btn-outline btn-xs gap-1" onClick={onRetry}>
            <ArrowPathIcon className="w-4" /> Retry
          </button>
        </div>
      )}
      {!isUser && chips.length > 0 && (
        <div className="mb-2 ml-1 flex flex-wrap gap-1">
          {chips.map((d) => {
            const key = directiveKey(d)
            return (
              <button
                key={key}
                className="badge badge-outline badge-sm cursor-pointer gap-1"
                onClick={() => dispatch(chatSlice.actions.focusGraph({ key }))}
              >
                <ChartBarIcon className="w-3.5" /> {graphLabel(d)}
              </button>
            )
          })}
        </div>
      )}
      {!isUser && message.evidence && message.evidence.length > 0 && (
        <div className="mb-2 ml-1">
          <EvidencePanel evidence={message.evidence} scopeType={scopeType} scopeId={scopeId} />
        </div>
      )}
    </>
  )
}
